
/**
 * Core banner generation functionality
 */

import html2canvas from 'html2canvas';
import { Banner, BannerTextLine } from '@/types/banner';

/**
 * Generate a banner image from a banner configuration
 */
export const generateBannerImage = async (banner: Banner, width: number, height: number): Promise<string> => {
  // Check if banner already has an imageUrl that's animated
  if (banner.imageUrl && banner.imageSettings?.isAnimated) {
    console.log("Returning animated GIF directly:", banner.imageUrl);
    return banner.imageUrl;
  }

  // Create a temporary div to render the banner
  const tempDiv = document.createElement('div');
  tempDiv.style.width = `${width}px`;
  tempDiv.style.height = `${height}px`;
  tempDiv.style.backgroundColor = banner.backgroundColor;
  tempDiv.style.position = 'relative';
  tempDiv.style.overflow = 'hidden';

  // Add image if present
  if (banner.imageUrl) {
    const img = document.createElement('img');
    img.src = banner.imageUrl;
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    img.style.position = 'absolute';
    img.style.top = '0';
    img.style.left = '0';
    img.style.opacity = banner.imageSettings?.opacity !== undefined 
      ? (banner.imageSettings.opacity / 100).toString() 
      : '1';
    tempDiv.appendChild(img);
    
    // Add cross-origin attribute to help with CORS issues
    img.crossOrigin = "anonymous";
  }

  // Add text content
  const textContainer = document.createElement('div');
  textContainer.style.position = 'relative';
  textContainer.style.zIndex = '2';
  textContainer.style.padding = '12px';
  textContainer.style.display = 'flex';
  textContainer.style.flexDirection = 'column';
  textContainer.style.justifyContent = 'center';
  textContainer.style.height = '100%';
  textContainer.style.textAlign = 'center';

  if (banner.textLines && banner.textLines.length > 0) {
    banner.textLines.forEach((line: BannerTextLine) => {
      if (!line.text) return;
      
      const textElement = document.createElement('p');
      textElement.textContent = line.text;
      textElement.style.fontFamily = line.fontFamily || 'Arial';
      textElement.style.fontSize = `${line.fontSize || 16}px`;
      textElement.style.color = line.color || banner.textColor || '#000000';
      textElement.style.fontWeight = line.bold ? 'bold' : 'normal';
      textElement.style.fontStyle = line.italic ? 'italic' : 'normal';
      textElement.style.margin = '4px 0';
      textContainer.appendChild(textElement);
    });
  } else if (banner.text) {
    const textElement = document.createElement('p');
    textElement.textContent = banner.text;
    textElement.style.color = banner.textColor || '#000000';
    textContainer.appendChild(textElement);
  }

  tempDiv.appendChild(textContainer);

  // Add border if specified
  if (banner.borderStyle && banner.borderStyle !== 'none') {
    tempDiv.style.border = `${banner.borderWidth || 1}px ${banner.borderStyle} ${banner.borderColor || '#000000'}`;
  }

  // Append to document temporarily (hidden)
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  document.body.appendChild(tempDiv);

  try {
    // Use html2canvas to create an image
    const canvas = await html2canvas(tempDiv, {
      width,
      height,
      scale: 2, // Higher scale for better quality
      useCORS: true,
      allowTaint: true,
      backgroundColor: banner.backgroundColor
    });
    
    // Convert to data URL
    const dataUrl = canvas.toDataURL('image/png');
    
    // Clean up
    document.body.removeChild(tempDiv);
    
    return dataUrl;
  } catch (error) {
    console.error('Error generating banner image:', error);
    document.body.removeChild(tempDiv);
    throw error;
  }
};

/**
 * Save a banner draft to local storage
 */
export const saveBannerDraft = (key: string, data: any): void => {
  try {
    localStorage.setItem(key, JSON.stringify({
      ...data,
      lastUpdated: Date.now()
    }));
  } catch (error) {
    console.error('Error saving banner draft:', error);
  }
};

/**
 * Load a banner draft from local storage
 */
export const loadBannerDraft = <T>(key: string): T | null => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error loading banner draft:', error);
    return null;
  }
};

/**
 * Delete a banner draft from local storage
 */
export const deleteBannerDraft = (key: string): void => {
  localStorage.removeItem(key);
};

/**
 * Check if a string is a valid data URL
 */
export const isDataURL = (str: string): boolean => {
  return !!str.match(/^data:image\/[a-z]+;base64,/i);
};

/**
 * Check if a URL points to an animated GIF
 * Note: This is a basic check based on file extension
 * A more robust solution would involve checking the actual file contents
 */
export const isAnimatedGifUrl = (url: string): boolean => {
  return url.toLowerCase().endsWith('.gif');
};
